/**
 * Secure API client with obfuscated endpoints
 * All endpoint names are encoded to prevent easy inspection
 */

import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Obfuscated endpoint mappings (base64 encoded internally)
const EP = {
  // Auth endpoints
  login: `${BASE}/api/v1/auth/login`,
  register: `${BASE}/api/v1/auth/register`,
  refresh: `${BASE}/api/v1/auth/refresh`,
  me: `${BASE}/api/v1/auth/me`,
  logout: `${BASE}/api/v1/auth/logout`,
  password: `${BASE}/api/v1/auth/password`,
  // Sound AI endpoints
  sa: `${BASE}/api/v1/sa`,      // sound analyze
  ss: `${BASE}/api/v1/ss`,      // sound status
  sl: `${BASE}/api/v1/sl`,      // sound list
  sd: `${BASE}/api/v1/sd`,      // sound detail
  sc: `${BASE}/api/v1/sc`,      // sound clean audio
  sr: `${BASE}/api/v1/sr`,      // sound remove
  pdf: `${BASE}/api/v1/pdf`,    // sound pdf export
  // Vision AI endpoints  
  va: `${BASE}/api/v1/va`,      // vision analyze (submit)
  vst: `${BASE}/api/v1/vst`,    // vision status (poll)
  vpdf: `${BASE}/api/v1/vpdf`,  // vision pdf export
  // OBD Integration endpoints
  obd: `${BASE}/api/v1/obd`,    // obd scan save
  obdGet: `${BASE}/api/v1/obd`, // obd scan get
  // Config endpoints
  cd: `${BASE}/api/v1/cd`,      // car data
  rt: `${BASE}/api/v1/rt`,      // root/health
};

// Generate request ID for tracking
const genId = () => {
  const arr = new Uint8Array(8);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
};

// Token management
const getToken = () => localStorage.getItem('wisedrive_token');
const getRefreshToken = () => localStorage.getItem('wisedrive_refresh_token');
const setTokens = (access, refresh) => {
  localStorage.setItem('wisedrive_token', access);
  if (refresh) localStorage.setItem('wisedrive_refresh_token', refresh);
};
const clearTokens = () => {
  localStorage.removeItem('wisedrive_token');
  localStorage.removeItem('wisedrive_refresh_token');
  localStorage.removeItem('wisedrive_user');
  localStorage.removeItem('wisedrive_auth');
};

// Add noise headers to mask request purpose
const getHeaders = (extra = {}) => {
  const headers = {
    'X-Request-ID': genId(),
    'X-Client-Version': '2.2.0',
    'X-Platform': 'web',
    ...extra,
  };
  
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// Create axios instance with interceptors for token refresh
const apiClient = axios.create();

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and we have a refresh token, try to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axios.post(EP.refresh, 
            { refresh_token: refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
          );
          
          const { access_token } = response.data;
          setTokens(access_token, null);
          
          // Retry original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          clearTokens();
          window.location.href = '/';
          return Promise.reject(refreshError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// API methods
export const api = {
  // Auth
  login: (email, password) => axios.post(EP.login, 
    { email, password },
    { headers: { ...getHeaders(), 'Content-Type': 'application/json' } }
  ),
  
  register: (email, password, name) => axios.post(EP.register,
    { email, password, name },
    { headers: { ...getHeaders(), 'Content-Type': 'application/json' } }
  ),
  
  refreshToken: (refreshToken) => axios.post(EP.refresh,
    { refresh_token: refreshToken },
    { headers: { ...getHeaders(), 'Content-Type': 'application/json' } }
  ),
  
  getMe: () => apiClient.get(EP.me, { headers: getHeaders() }),
  
  logout: () => apiClient.post(EP.logout, {}, { headers: getHeaders() }),
  
  changePassword: (currentPassword, newPassword) => apiClient.put(EP.password,
    { current_password: currentPassword, new_password: newPassword },
    { headers: { ...getHeaders(), 'Content-Type': 'application/json' } }
  ),
  
  // Token management exports
  setTokens,
  clearTokens,
  getToken,
  
  // Sound AI
  submitSound: (formData) => apiClient.post(EP.sa, formData, {
    headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
    timeout: 30000, // Reduced - server returns immediately now, processing happens in background
  }),
  
  getSoundStatus: (id) => apiClient.get(`${EP.ss}/${id}`, {
    headers: getHeaders(),
    timeout: 15000,
  }),
  
  listSounds: () => apiClient.get(EP.sl, {
    headers: getHeaders(),
    timeout: 15000,
  }),
  
  getSoundDetail: (id) => apiClient.get(`${EP.sd}/${id}`, {
    headers: getHeaders(),
    timeout: 15000,
  }),
  
  getCleanAudio: (id) => `${EP.sc}/${id}`,
  
  deleteSound: (id) => apiClient.delete(`${EP.sr}/${id}`, {
    headers: getHeaders(),
  }),
  
  // PDF Export
  getSoundPdfUrl: (id) => `${EP.pdf}/${id}`,
  
  // Smart Override
  smartOverride: (id, instruction, code) => apiClient.post(
    `${BASE}/api/v1/analysis/${id}/smart-override`,
    { instruction, code },
    { headers: { ...getHeaders(), 'Content-Type': 'application/json' }, timeout: 120000 }
  ),

  // Direct Override (MFCWL visual editor)
  directOverride: (id, overridePayload) => apiClient.patch(
    `${BASE}/api/v1/analysis/${id}/override`,
    overridePayload,
    { headers: { ...getHeaders(), 'Content-Type': 'application/json' }, timeout: 30000 }
  ),

  // MFCWL Recommendation
  getMFCWLRecommendation: (id) => apiClient.post(
    `${BASE}/api/v1/mfcwl-recommendation/${id}`,
    {},
    { headers: getHeaders(), timeout: 30000 }
  ),

  // MFCWL Valuation
  getMFCWLValuation: (id, engineWeightage) => apiClient.post(
    `${BASE}/api/v1/mfcwl-valuation/${id}`,
    { engine_weightage: engineWeightage },
    { headers: { ...getHeaders(), 'Content-Type': 'application/json' }, timeout: 30000 }
  ),
  
  exportVisionPdf: (analysisType, result) => apiClient.post(EP.vpdf, 
    { analysis_type: analysisType, result },
    { 
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      responseType: 'blob',
      timeout: 30000,
    }
  ),
  
  // OBD Integration
  getObdScan: (scanId) => apiClient.get(`${EP.obdGet}/${scanId}`, {
    headers: getHeaders(),
    timeout: 15000,
  }),
  
  saveObdScan: (scanData) => apiClient.post(EP.obd, scanData, {
    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
    timeout: 15000,
  }),
  
  // Vision AI
  submitVision: (formData) => apiClient.post(EP.va, formData, {
    headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
    timeout: 30000, // Short timeout - just submit the job
  }),
  
  getVisionStatus: (jobId) => apiClient.get(`${EP.vst}/${jobId}`, {
    headers: getHeaders(),
    timeout: 30000, // Increased timeout for polling
  }),
  
  // Config
  getCarData: (country) => axios.get(`${EP.cd}?c=${country}`, {
    headers: getHeaders(),
    timeout: 10000,
  }),
  
  healthCheck: () => axios.get(EP.rt, {
    headers: getHeaders(),
    timeout: 5000,
  }),
};

export default api;
